import * as THREE from 'three';
import { FishBatch, facesRight, fishAtlas, type FishDraw } from '../fishBatch';
import { hash3 } from '../fishSwim';
import { pickNearest } from '../pick';
import { DOLPHIN_SPRITES } from '../../ui/data/dolphinSprites';
import { SPINNER_DOLPHIN } from '../../sim/dolphins';
import type { ReefDolphins } from './data';

/**
 * The spinner dolphins resting over Maui Nui Reef (M216, the user's word: "figure out how to display the dolphins based
 * on best available data"): on a day a group rests over the scene's hectare (`reefDolphinsOn`), its dolphins there,
 * each a sprite, the user's two pictures, in its hours inshore. As they rest: "Rest areas are generally 50 meters or
 * less in depth", the group coming in by morning and resting through the middle of the day (Norris and Dohl 1980,
 * *Fishery Bulletin* 77, p. 821, public domain), "dolphins mainly rest between 10.00 and 16.00" (Tyne et al. 2017, p. 5,
 * CC BY), so they swim slowly together, a group moving as one.
 *
 * Each a rendering choice (JUDGED unless said):
 * - The group's middle swims a wide loop about the home view's middle, `loopM` across and `loopM / 2` along the view, or
 *   less where the home view sees less across there, so the group, its spread with it, stays within `seenShare` of the
 *   part of the screen the panels leave; and where its spread alone is wider than that, about a point farther along the
 *   view, as far as fits it, as the whale's lane is (M210; M216's review: at 16 m, 59 to 71% of the dolphins' time was
 *   off screen), at
 *   `restMPerS`, under the "Travel speed was typically 3.2 km h−1" (0.89 m/s; Tyne et al. 2017, Table 1, from Norris et
 *   al. 1994, compiled) as they "moved slowly back and forth" at rest (Norris and Dohl 1980, p. 840), no resting speed
 *   measured; the dolphins each its own place in it, within `spreadM` of its middle times the square root of its count
 *   over `spreadOf`'s ("a school of 30 animals ... might become concentrated in a 20 m diameter disc", p. 840), `bobM` up
 *   and down over `bobS`.
 * - All of it within the Reef camera's box, so a follow reaches it (M229, the redesign session's reviewer: outside it 100%
 *   of the time on a phone held upright): where the box ends before the view leaves the group room, its dolphins drawn
 *   closer together, as far back as the box allows and as wide as the view leaves it there. MEASURED (a probe of the
 *   on-screen test's setup, 11 dolphins, every fifth frame of two minutes): each dolphin's sprite 36% hidden by nearer
 *   ones on a phone upright, 34% on a computer, 49% on a phone on its side, against M216's 18%, 18% and 43% outside the box; the
 *   swing along the view shortened instead to 1 m, 36%, 31% and 45%, the group then turning back every few seconds, not
 *   taken.
 * - Each `bodiesAbove` of its lengths over the floor, its own draw between the two ("leveled out a few body lengths above
 *   the bottom and moved slowly along, schooled tightly", p. 841), and `belowSurfaceM` under the surface at least; and no
 *   higher on the screen than `seenShare` of the part the panels leave, lowered toward its floor's least where it would
 *   be (M216's review: the home view looking 25 degrees down, 27 to 32% of their time on a computer was over its top).
 * - Drawn its true length, `SPINNER_DOLPHIN.lengthM`; its two pictures each held `strokeS`, its own phase.
 * - Fading in over `fadeS` as its hours inshore begin, out as they end.
 * - Drawn solid, writing its depth, as the whale over the reef is (M210), so what is beyond a dolphin is hidden by it and
 *   what is nearer drawn over it; none written while it is less than half seen.
 */
export const REEF_DOLPHINS = { loopM: 16, seenShare: 0.8, restMPerS: 0.6, spreadM: 10, spreadOf: 30, bobM: 0.3, bobS: 9, bodiesAbove: [1, 3], belowSurfaceM: 1.5, strokeS: 0.45, fadeS: 3 } as const;

/** A dolphin as drawn, for its tap and its card. */
export interface DolphinDrawn {
  /** Its name in the scene: its place in the day's group. */
  id: string;
  middle: THREE.Vector3;
  width: number;
  height: number;
  alpha: number;
}

/** The day's dolphins over a reef scene's floor, about its home view's middle. */
export class ReefDolphinPod {
  readonly group = new THREE.Group();
  private readonly batch: FishBatch;
  private readonly rects: readonly (readonly [number, number, number, number])[];
  private readonly along: THREE.Vector3;
  private readonly across: THREE.Vector3;
  private day: ReefDolphins | null = null;
  private hour = 0;
  private alpha = 0;
  /** The group's way round its loop, radians, and the seconds swum (for the strokes and the bob). */
  private turn = 0;
  private seconds = 0;
  private camera: THREE.Camera | null = null;
  /** The part of the screen the panels leave, normalized device coordinates (the whole until given). */
  private visible: readonly [number, number, number, number] = [-1, 1, -1, 1];
  private drawn: DolphinDrawn[] = [];
  private right: boolean[] = [];
  /**
   * Whether the group has swum since it was last drawn: drawn once a frame, by `face` (the scene's `fit`, after its
   * `animate`), or before it by whatever reads it first (a follow's), not again by each.
   */
  private swum = false;

  constructor(private readonly water: { middle: THREE.Vector3; eye: THREE.Vector3; floorAt: (x: number, z: number) => number; box: { min: readonly number[]; max: readonly number[] } }) {
    const { texture, rects } = fishAtlas([...DOLPHIN_SPRITES.dolphin]);
    this.rects = DOLPHIN_SPRITES.dolphin.map((s) => rects.get(s) as [number, number, number, number]);
    this.batch = new FishBatch(texture, true, true, true);
    this.batch.mesh.renderOrder = 3.4;
    const depth = this.batch.depthPass as THREE.Mesh;
    depth.renderOrder = 3.45;
    this.group.add(this.batch.mesh, depth);
    const look = new THREE.Vector3(water.middle.x - water.eye.x, 0, water.middle.z - water.eye.z);
    this.along = look.lengthSq() > 1e-12 ? look.normalize() : new THREE.Vector3(0, 0, -1);
    this.across = new THREE.Vector3(-this.along.z, 0, this.along.x);
  }

  /** The day's group, or none. */
  set(day: ReefDolphins | null): void {
    // The group gone with the day kept as it was while it fades (M266: on a day without it, it had vanished at once).
    if (this.day) this.last = this.day;
    this.day = day;
    this.draw();
  }

  /** The last day's group, drawn as it fades out on a day without one (M266). */
  private last: ReefDolphins | null = null;

  setHour(hour: number): void {
    this.hour = hour;
  }

  /** Whether the group is inshore at the hour. */
  here(): boolean {
    return this.day !== null && this.hour >= this.day.hours[0] && this.hour < this.day.hours[1];
  }

  /** Moved on by `seconds` round their loop, faded toward being there or gone; still when `calm`. */
  swim(seconds: number, calm = false): void {
    this.alpha = this.here() ? Math.min(1, this.alpha + seconds / REEF_DOLPHINS.fadeS) : Math.max(0, this.alpha - seconds / REEF_DOLPHINS.fadeS);
    if (!calm) {
      this.seconds += seconds;
      // The loop's length about its ellipse, nearly (Ramanujan's first), as wide across as the view leaves it.
      const [a, b] = [Math.max(0.01, this.loopOf((this.day ?? this.last)?.count ?? 0).half), REEF_DOLPHINS.loopM / 4];
      const loop = Math.PI * (3 * (a + b) - Math.sqrt((3 * a + b) * (a + 3 * b)));
      this.turn += (2 * Math.PI * seconds * REEF_DOLPHINS.restMPerS) / loop;
    }
    this.swum = true;
  }

  face(camera: THREE.Camera, visible?: readonly [number, number, number, number]): void {
    this.camera = camera;
    if (visible) this.visible = visible;
    this.draw();
  }

  /**
   * The loop's place: its middle's distance along the view from the eye, m, its half-width across, m, and the share of
   * its middles' spread the group is drawn at: at the home view's middle, `loopM / 2` or as much of it as leaves the group
   * within `seenShare` of the view across; where the group is wider than that, at no width, as far along as fits it; and
   * all of it within the camera's box (M229), drawn closer together where the box ends before the view leaves the group
   * room, as wide as the view leaves it at the farthest the box allows (its bodies' half-length ends squeezed with its
   * spread, though they are drawn whole: an outermost one's end up to about 0.14 m past a phone's edge at the loop's
   * nearest, M229's second review, against a group a third tighter were they kept).
   */
  private loopOf(n: number): { away: number; half: number; squeeze: number } {
    const c = this.camera;
    const f = REEF_DOLPHINS;
    const middle = Math.hypot(this.water.middle.x - this.water.eye.x, this.water.middle.z - this.water.eye.z);
    if (!(c instanceof THREE.PerspectiveCamera)) return { away: middle, half: f.loopM / 2, squeeze: 1 };
    // The group's middles within `spreadM`'s share of its middle, and its bodies' ends half a length beyond.
    const spread = f.spreadM * Math.sqrt(n / f.spreadOf) + SPINNER_DOLPHIN.lengthM / 2;
    // Seen across a meter's distance, both ways, within the part the panels leave.
    const perM = Math.max(1e-6, f.seenShare * Math.tan(((c.fov / 2) * Math.PI) / 180) * c.aspect * ((this.visible[1] - this.visible[0]) / 2));
    // The loop's far end, `loopM / 2` along beyond its near point, and the group beyond that, before the box's edge.
    const room = Math.max(0, this.boxAhead() - f.loopM / 2);
    let away = Math.max(middle, spread / perM);
    let squeeze = 1;
    if (away + spread > room) {
      away = room - spread >= spread / perM ? room - spread : room / (1 + perM);
      squeeze = Math.min(1, (perM * away) / spread);
    }
    return { away, half: Math.max(0, Math.min(f.loopM / 2, perM * away - spread * squeeze)), squeeze };
  }

  /** How far along the view from the eye the camera's box ends, m, across the water (x and z). */
  private boxAhead(): number {
    const { eye } = this.water;
    const { min, max } = this.water.box;
    let ahead = Infinity;
    for (const [d, e, lo, hi] of [[this.along.x, eye.x, min[0], max[0]], [this.along.z, eye.z, min[2], max[2]]] as const) {
      if (d > 1e-9) ahead = Math.min(ahead, ((hi as number) - e) / d);
      else if (d < -1e-9) ahead = Math.min(ahead, ((lo as number) - e) / d);
    }
    return ahead;
  }

  private draw(): void {
    this.swum = false;
    const pod = this.day ?? this.last;
    const n = this.alpha > 0 && pod ? pod.count : 0;
    const f = REEF_DOLPHINS;
    const loop = this.loopOf(n);
    // Along the view always its `loopM / 4` either way, back and forth, where across it may have no room.
    const [a, b] = [loop.half, f.loopM / 4];
    const e = this.water.eye;
    // The loop's nearest point at the fitted distance, the rest of it beyond (its near end under the view's bottom edge otherwise).
    const m = new THREE.Vector3(e.x + this.along.x * (loop.away + b), this.water.middle.y, e.z + this.along.z * (loop.away + b));
    const cx = m.x + this.across.x * a * Math.cos(this.turn) + this.along.x * b * Math.sin(this.turn);
    const cz = m.z + this.across.z * a * Math.cos(this.turn) + this.along.z * b * Math.sin(this.turn);
    // The group's way, along its loop.
    const way = { x: -this.across.x * a * Math.sin(this.turn) + this.along.x * b * Math.cos(this.turn), z: -this.across.z * a * Math.sin(this.turn) + this.along.z * b * Math.cos(this.turn) };
    const sprite = DOLPHIN_SPRITES.dolphin[0];
    const width = SPINNER_DOLPHIN.lengthM;
    const height = (width * sprite.height) / sprite.width;
    const draws: FishDraw[] = [];
    const drawn: DolphinDrawn[] = [];
    for (let k = 0; k < n; k++) {
      const r = loop.squeeze * f.spreadM * Math.sqrt(n / f.spreadOf) * Math.sqrt(hash3(k, 1, 216));
      const t = 2 * Math.PI * hash3(k, 2, 216);
      const x = cx + r * Math.cos(t);
      const z = cz + r * Math.sin(t);
      const floor = this.water.floorAt(x, z);
      const above = (f.bodiesAbove[0] + (f.bodiesAbove[1] - f.bodiesAbove[0]) * hash3(k, 3, 216)) * width;
      const bob = f.bobM * Math.sin((2 * Math.PI * this.seconds) / f.bobS + 6 * hash3(k, 4, 216));
      const y = this.underTop(x, z, floor + f.bodiesAbove[0] * width + height / 2, Math.min(-f.belowSurfaceM - height / 2, floor + above + height / 2), height) + bob;
      const at = new THREE.Vector3(x, y, z);
      const right = facesRight(this.camera, at, way, this.right[k] ?? way.x > 0);
      this.right[k] = right;
      const frame = Math.floor(this.seconds / f.strokeS + 2 * hash3(k, 5, 216)) % 2;
      drawn.push({ id: `dolphin:${k}`, middle: at, width, height, alpha: this.alpha });
      draws.push({ x, y: y - height / 2, z, width, height, rect: this.rects[frame] as [number, number, number, number], right, alpha: this.alpha });
    }
    this.drawn = drawn;
    this.batch.set(draws);
  }

  /**
   * A height no higher than leaves its top within `seenShare` of the screen's upper part the panels leave, between `low`
   * (its least over the floor, kept whatever the view) and `want`: halved toward `low` until it is.
   */
  private underTop(x: number, z: number, low: number, want: number, height: number): number {
    const c = this.camera;
    if (!c || want <= low) return want;
    const top = this.visible[3] * REEF_DOLPHINS.seenShare;
    const p = new THREE.Vector3();
    const over = (y: number): boolean => p.set(x, y + height / 2, z).project(c).y > top;
    if (!over(want)) return want;
    if (over(low)) return low;
    let [lo, hi] = [low, want];
    for (let i = 0; i < 12; i++) {
      const mid = (lo + hi) / 2;
      if (over(mid)) hi = mid;
      else lo = mid;
    }
    return lo;
  }

  drawnNow(): DolphinDrawn[] {
    if (this.swum) this.draw();
    return this.drawn;
  }

  /** The dolphin under a point on the screen (normalized device coordinates); null for none. */
  pick(camera: THREE.Camera, x: number, y: number, widthPx: number, heightPx: number, withinPx: number): DolphinDrawn | null {
    // None of a pod fading out, gone with its day (review 31: tapped, its card could not be told, the follow ended at once).
    return this.here() ? pickNearest(this.drawnNow(), camera, x, y, widthPx, heightPx, withinPx) : null;
  }

  drawnOf(id: string): DolphinDrawn | null {
    return this.drawnNow().find((d) => d.id === id) ?? null;
  }

  fogUniforms(): ReturnType<FishBatch['fogUniforms']> {
    return this.batch.fogUniforms();
  }

  setLight(r: number, g: number, b: number): void {
    this.batch.setLight(r, g, b);
  }

  /** What the batch draws, for the tests. */
  draws(): FishDraw[] {
    if (this.swum) this.draw();
    return this.batch.drawn();
  }

  dispose(): void {
    this.batch.dispose();
  }
}
